import React from 'react';
import { useTranslation } from 'react-i18next';
import { RiPencilLine, RiEqualizerLine, RiExpandVerticalLine, RiCollapseVerticalLine } from '@remixicon/react';
import CardShell from '../../../components/CardShell';
import Listbox from '../../../components/Listbox';
import { OfferIcon, iconOf } from '../offerIcons';
import { MOIS_12, sumNN, realLigne, realOffre } from './model';
import { addForecastLine } from '../../../api/salesOfferMonths';

/* LA VUE GLOBALE (décisions d'Olivier, 2026-10-21 ; proto `WIP/protos/previsions-alt-a3v4-totaux.html`) :
   le chiffre d'affaires de toutes les offres, mois par mois. Les mois clos portent le CA réalisé, les mois à
   venir le CA reconnu du plan ; puis le total de l'exercice, le report, la cible et l'écart. Un clic sur une
   offre ou sur une de ses lignes ouvre sa grille ; un chevron déplie ses lignes ; le stylo (au survol) et le
   clic droit ouvrent la fenêtre de l'offre. Sous le total, la comptabilité à fin du dernier mois clos et l'écart
   avec elle quand Finance répond.
   LE TOTAL RÉALISÉ SUIT LE DERNIER MOIS CLOS, LE TOTAL PRÉVISIONS SUIT DÉCEMBRE (demande d'Olivier, 2026-10-22),
   comme dans la grille d'une offre. */

const MOIS = (p) => [p('Jan'), p('Fév'), p('Mar'), p('Avr'), p('Mai'), p('Juin'),
                     p('Juil'), p('Août'), p('Sep'), p('Oct'), p('Nov'), p('Déc')];

export default function GlobalCard({ c }) {
  const { p, tid, year, canWrite, fmt, vue, realise, appel, ouvertes, setOuvertes, choisir, ouvrirMenu, ouvrirOffre,
          ecartCompta, caReel, reprise, ouvrirProfils } = c;
  const { i18n } = useTranslation();
  const NA = <span className="om-na">{p('n/a')}</span>;
  const noms = MOIS(p);
  const ouverts = vue.open.filter((i) => i < 12);
  const clos = MOIS_12.filter((i) => !ouverts.includes(i));
  const seuil = c.unit === 'kchf' ? 500 : 0.5;
  const premierOuvert = ouverts[0];
  const ecart = (v) => {
    if (v == null) return NA;
    if (Math.abs(v) < seuil) return '–';
    return <span className={v > 0 ? 'om-pos' : 'om-neg'}>{`${v > 0 ? '+' : ''}${fmt.val(v)}`}</span>;
  };
  const rows = vue.rows;
  const avecLignes = rows.some((r) => r.has_lines);
  const toutOuvert = avecLignes && rows.filter((r) => r.has_lines).every((r) => ouvertes.has(r.offer_id));
  const toutBasculer = () => setOuvertes(toutOuvert ? new Set() : new Set(rows.filter((r) => r.has_lines).map((r) => r.offer_id)));
  const basculer = (id) => setOuvertes((x) => { const n = new Set(x); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const reconnaissance = (r) => {
    const t = r.terms || {};
    return t.recognition === 'spread' && t.period_months > 1 ? `${p('étalé sur')} ${t.period_months} ${p('mois')}` : p('à la livraison');
  };
  const offresAjout = rows.filter((r) => r.equation !== 'base_rate');

  /* Les douze mois d'une grandeur : le réalisé sur les mois clos (null : inconnu), le plan sur les autres. */
  const douze = (reel, plan) => MOIS_12.map((i) => (clos.includes(i) ? (reel ? reel[i] : null) : (plan ? plan[i] : null)));
  const cellMois = (v, i, k) => {
    const estClos = clos.includes(i);
    const sep = i === (clos.length ? clos[0] : premierOuvert) || i === premierOuvert;
    return (
      <td key={k} className={`lk-num ${estClos ? 'pv-clos' : 'pv-prev'}${sep ? ' lk-sep' : ''}`}>
        {v == null ? (estClos ? NA : '–') : fmt.val(v)}
      </td>
    );
  };
  /* Le Total réalisé s'insère après le dernier mois clos, le Total prévisions après décembre. */
  const avecTotaux = (cells, tr, tp) => [
    ...cells.slice(0, clos.length),
    ...(clos.length > 0 ? [tr] : []),
    ...cells.slice(clos.length),
    ...(ouverts.length > 0 ? [tp] : []),
  ];
  const tdTot = (v, key, cls = '') => (
    <td key={key} className={`lk-num om-fort ${cls}`}>{v === undefined ? '' : (v == null ? NA : fmt.val(v))}</td>
  );
  const tails = (tot, report, cible) => (
    <>
      <td className="lk-num lk-sep om-fort">{tot == null ? NA : fmt.val(tot)}</td>
      <td className="lk-num om-der">{report == null ? '' : fmt.val(report)}</td>
      <td className="lk-num lk-sep">{cible == null ? NA : fmt.val(cible)}</td>
      <td className="lk-num">{cible == null || tot == null ? '' : ecart(tot - cible)}</td>
    </>
  );

  const T = Array(12).fill(0);
  let totCible = 0;
  let cibleConnue = true;
  let totReport = 0;
  const lignes = [];
  rows.forEach((r) => {
    const ro = realOffre(realise, r);
    const mois = douze(ro.ca, r.ca);
    const reelTot = sumNN(ro.ca, clos);
    const planTot = sumNN(r.ca, ouverts) || 0;
    const tot = (reelTot || 0) + planTot;
    const report = (r.carry || {}).ca ?? 0;
    const cible = r.target ? r.target.value : null;
    MOIS_12.forEach((i) => { T[i] += mois[i] || 0; });
    if (cible == null) cibleConnue = false; else totCible += cible;
    totReport += report;
    const ouverte = ouvertes.has(r.offer_id);
    lignes.push(
      <tr key={r.offer_id} className={`om-row om-offre pv-offre${ouverte ? ' is-open' : ''}`}
          onClick={() => choisir(r.offer_id)} onContextMenu={(e) => ouvrirMenu(e, r)}>
        <td className="om-lbl lk-stick" style={{ '--stick': '0px' }} title={r.name}>
          <OfferIcon name={iconOf(r)} size={15} />{r.name}
          {canWrite && (
            <button type="button" className="pv-pen pv-pen--survol" title={p("Modifier l'offre")} aria-label={p("Modifier l'offre")}
                    onClick={(e) => { e.stopPropagation(); ouvrirOffre(r); }}><RiPencilLine size={14} aria-hidden="true" /></button>
          )}
          {r.has_lines && (
            <span className={`om-pli${ouverte ? ' is-open' : ''}`} role="button" aria-label={ouverte ? p('Replier les lignes') : p('Déplier les lignes')}
                  onClick={(e) => { e.stopPropagation(); basculer(r.offer_id); }}>›</span>
          )}
        </td>
        <td className="om-lbl pv-mu">{reconnaissance(r)}</td>
        {avecTotaux(MOIS_12.map((i) => cellMois(mois[i], i, i)), tdTot(reelTot, 'tr', 'pv-clos'), tdTot(planTot, 'tp', 'pv-prev'))}
        {tails(tot, report, cible)}
      </tr>,
    );
    if (r.has_lines && ouverte) {
      const reelles = r.lines.filter((l) => !l.realise_seul);
      reelles.forEach((l) => {
        const rl = realLigne(realise, r, l);
        const mL = douze(rl.ca, l.ca);
        const tL = (sumNN(rl.ca, clos) || 0) + (sumNN(l.ca, ouverts) || 0);
        lignes.push(
          <tr key={`${r.offer_id}-${l.line_id}`} className="om-row om-li pv-li" onClick={() => choisir(r.offer_id, l.line_id)}>
            <td className="om-lbl lk-stick" style={{ '--stick': '0px' }} title={l.label}>{l.label}</td>
            <td />
            {avecTotaux(MOIS_12.map((i) => cellMois(mL[i], i, i)), tdTot(sumNN(rl.ca, clos), 'tr', 'pv-clos'), tdTot(sumNN(l.ca, ouverts) || 0, 'tp', 'pv-prev'))}
            {tails(tL, (l.carry || {}).ca ?? 0, l.target ? l.target.value : null)}
          </tr>,
        );
      });
      const na = ro.nonAffecte.ca;
      if (na && Math.abs(sumNN(na, clos) || 0) > seuil) {
        lignes.push(
          <tr key={`${r.offer_id}-na`} className="om-row om-li pv-li pv-na">
            <td className="om-lbl lk-stick pv-mu" style={{ '--stick': '0px' }}>{p('Non affecté')}</td>
            <td />
            {avecTotaux(MOIS_12.map((i) => cellMois(clos.includes(i) ? na[i] : null, i, i)), tdTot(sumNN(na, clos), 'tr', 'pv-clos pv-mu'), <td key="tp" />)}
            <td /><td /><td /><td />
          </tr>,
        );
      }
    }
  });
  const totTot = T.reduce((a, b) => a + b, 0);
  const avecReprise = reprise != null && Math.abs(reprise) >= seuil;
  const aligne = totTot + (ecartCompta || 0) + (avecReprise ? reprise : 0);
  const cibleTot = cibleConnue ? totCible : null;
  const vides = (n) => Array.from({ length: n }, (_, k) => <td key={k} />);
  /* Le mois du dernier arrêté : la comptabilité se lit « à fin » de ce mois, jamais du suivant. */
  const moisFin = clos.length
    ? new Intl.DateTimeFormat(i18n.language === 'en' ? 'en' : 'fr', { month: 'long' }).format(new Date(2000, clos[clos.length - 1], 1))
    : '';

  const barre = (
    <>
      {canWrite && offresAjout.length > 0 && (
        <Listbox className="om-addligne" value="" placeholder={`＋ ${p('Ligne')}`}
                 options={offresAjout.map((r) => ({ value: r.offer_id, label: r.name }))}
                 onChange={(id) => { if (id) appel(addForecastLine(tid, id, p('Nouvelle ligne'))); }} />
      )}
      {ouvrirProfils && (
        <button type="button" className="lk-btn lk-btn--toggle" onClick={ouvrirProfils}>
          <RiEqualizerLine aria-hidden="true" />{p('Modifier les profils')}
        </button>
      )}
    </>
  );
  return (
    <CardShell title={`${p("Chiffre d'affaires")} ${year} · ${c.dev}`} flush primaryAction={barre}
               viewControls={avecLignes ? (
                 <button type="button" className="lk-btn lk-btn--toggle" aria-pressed={toutOuvert} onClick={toutBasculer}>
                   {toutOuvert ? <RiCollapseVerticalLine aria-hidden="true" /> : <RiExpandVerticalLine aria-hidden="true" />}
                   {toutOuvert ? p('Tout replier') : p('Tout déplier')}
                 </button>
               ) : null}>
      <div className="om-scroll lk-hscroll">
        <table className="lk-entry-table om-table pv-grille pv-globale">
          <colgroup>
            <col style={{ width: 236 }} /><col style={{ width: 112 }} />
            {MOIS_12.map((i) => <col key={i} style={{ width: c.unit === 'kchf' ? 60 : 82 }} />)}
            {[...(clos.length > 0 ? ['tr'] : []), ...(ouverts.length > 0 ? ['tp'] : []), 0, 1, 2, 3].map((i) => (
              <col key={i} style={{ width: c.unit === 'kchf' ? 76 : 92 }} />
            ))}
          </colgroup>
          <thead>
            <tr className="om-bandes">
              <th /><th />
              {clos.length > 0 && <th className="lk-band lk-band--real" colSpan={clos.length + 1}>{p('Réalisé')} <small>· {p('mois clos')}</small></th>}
              {ouverts.length > 0 && <th className="lk-band lk-band--fore" colSpan={ouverts.length + 1}>{p('Planification')} <small>· {p('CA reconnu')}</small></th>}
              <th colSpan={4} />
            </tr>
            <tr>
              <th className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{p('Offre · ligne')}</th>
              <th className="om-lbl">{p('Reconnaissance')}</th>
              {avecTotaux(MOIS_12.map((i) => (
                <th key={i} className={`${clos.includes(i) ? 'pv-pass' : 'pv-prev'}${i === premierOuvert || (clos.length && i === clos[0]) ? ' lk-sep' : ''}`}>{noms[i]}</th>
              )),
              <th key="tr" className="om-th2 pv-pass">{p('Total')}<br />{p('réalisé')}</th>,
              <th key="tp" className="om-th2 pv-prev">{p('Total')}<br />{p('prévisions')}</th>)}
              <th className="om-th2 lk-sep">{p('Total')}<br />{year}</th>
              <th className="om-th2">{p('Report')}<br />{Number(year) + 1}</th>
              <th className="om-th2 lk-sep">{p('Cible')}</th>
              <th className="om-th2">{p('Écart')}</th>
            </tr>
          </thead>
          <tbody>
            {lignes}
            <tr className="om-row om-tot">
              <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{p('Total')}</td>
              <td />
              {avecTotaux(MOIS_12.map((i) => cellMois(T[i], i, i)), tdTot(clos.reduce((a, i) => a + T[i], 0), 'tr', 'pv-clos'), tdTot(ouverts.reduce((a, i) => a + T[i], 0), 'tp', 'pv-prev'))}
              {tails(totTot, totReport, cibleTot)}
            </tr>
            {(ecartCompta != null || avecReprise) && (
              <>
                {ecartCompta != null && (
                  <tr className="om-row om-compta">
                    <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{p('Écart avec la comptabilité')}</td>
                    <td />{avecTotaux(vides(12), tdTot(ecartCompta, 'tr', 'pv-clos'), <td key="tp" />)}
                    <td /><td /><td /><td />
                  </tr>
                )}
                {avecReprise && (
                  <tr className="om-row om-compta">
                    <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{p("Reprise d'acomptes clients")}</td>
                    <td />{avecTotaux(vides(12), <td key="tr" />, tdTot(reprise, 'tp', 'pv-prev'))}
                    <td /><td /><td /><td />
                  </tr>
                )}
                <tr className="om-row om-tot">
                  <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{p('Total, aligné sur la comptabilité')}</td>
                  <td />{avecTotaux(vides(12), caReel != null && clos.length > 0 ? tdTot(caReel, 'tr', 'pv-clos') : <td key="tr" />, <td key="tp" />)}
                  {tails(aligne, null, cibleTot)}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}
